import React from "react";
import { IoIosCheckmark } from "@hacknug/react-icons/io";
import { getMarkedForRemoval, markForRemoval } from "utils/data";
import get from "lodash/get";
import set from "lodash/set";
import snakeCase from "lodash/snakeCase";
import isEmpty from "lodash/isEmpty";
import random from "lodash/random";
import isEqual from "lodash/isEqual";
import orderBy from "lodash/orderBy";
import cloneDeep from "lodash/cloneDeep";
import flatten from "lodash/flatten";
import { COLORS } from "utils/styles";
import { FEATURE } from "utils/constants/models";
import {
  FEATURE_GROUP_LIST_DESTINATIONS,
  reorder,
  deconstructSourceAndDestination
} from "utils/drag";
import { TOGGLE_STATE } from "utils/constants/form";
import Analytics from "utils/analytics";
import { v4 as uuidv4 } from "uuid";
import { getEntitlementConfigTemplate } from "./entitlements";
import { getUUID } from "./uuid";
import { FEATURE_FIELDS } from "./constants/feature";

const DEFAULT_ORDER_BY = [["title"], ["asc"]];
const COMMON_KEY = "common";

export const addFeatureGroups = ({
  values,
  setFieldValue,
  setFieldTouched,
  tracking,
  sort
}) => {
  return (newFeatureGroups) => {
    const { featureGroups } = values;
    const updatedFeatureGroups = [...featureGroups];

    newFeatureGroups.forEach((featureGroup) => {
      updatedFeatureGroups.unshift(featureGroup);
    });

    const orderedFeatureGroups = sort
      ? applyFormSettingsToCollection({
          collection: updatedFeatureGroups,
          form: {
            orderBy: DEFAULT_ORDER_BY
          }
        })
      : updatedFeatureGroups;

    tracking && Analytics.trackEventWithData(tracking);

    setFieldValue("featureGroups", orderedFeatureGroups);
    setFieldTouched("featureGroups", true);
  };
};

const reduceFeaturesDifferences = (initial, current) => {
  const modelsMarkedForRemoval = getMarkedForRemoval(initial, current);
  return current.reduce((memo, currentModel) => {
    let returnModel = {};

    if (currentModel.id) {
      const modelMatch = initial.find(
        (initialFeature) => initialFeature.id === currentModel.id
      );

      FEATURE_FIELDS.forEach(({ key }) => {
        if (currentModel[key] && currentModel[key] !== get(modelMatch, key)) {
          returnModel[key] = currentModel[key];
        }
      });

      if (!isEmpty(returnModel)) {
        returnModel.id = currentModel.id;
        returnModel.uuid = currentModel.uuid;
      }
    } else {
      returnModel = {
        ...currentModel
      };
    }

    if (!isEmpty(returnModel)) {
      memo.push(returnModel);
    }

    return memo;
  }, modelsMarkedForRemoval);
};

/**
 * The models without ids are new
 * The models missing from current which had ids are marked to be destroy
 * @param {Object} initial
 * @param {Array} initial.features
 * @param {Object} current
 * @param {Array} current.features
 */
export const bulkUpdateFeaturesParams = (initial, current) => {
  const features = reduceFeaturesDifferences(
    initial.features,
    current.features
  );

  return {
    features
  };
};

const reduceFeatureGroupsDifferences = (initial, current) => {
  const modelsMarkedForRemoval = getMarkedForRemoval(initial, current);

  return current.reduce((memo, currentModel) => {
    let returnModel = {};
    if (currentModel.id) {
      const modelMatch = initial.find(
        (initialFeature) => initialFeature.id === currentModel.id
      );
      if (
        currentModel.title &&
        currentModel.title !== get(modelMatch, "title")
      ) {
        returnModel.title = currentModel.title;
      }

      if (
        currentModel.description &&
        currentModel.description !== get(modelMatch, "description")
      ) {
        returnModel.description = currentModel.description;
      }

      if (currentModel.feature_uuids) {
        const currentModelUIDS = currentModel.feature_uuids;
        const matchModelUIDS = get(modelMatch, "feature_uuids", []);
        const equalUIDS = isEqual(currentModelUIDS, matchModelUIDS);

        if (!equalUIDS) {
          returnModel.feature_uuids = currentModel.feature_uuids;
        }
      }

      if (!isEmpty(returnModel)) {
        returnModel.id = currentModel.id;
        returnModel.uuid = currentModel.uuid;
      }
    } else {
      returnModel = {
        ...currentModel
      };
    }

    if (!isEmpty(returnModel)) {
      memo.push(returnModel);
    }

    return memo;
  }, modelsMarkedForRemoval);
};

/**
 * The models without ids are new
 * The models missing from current which had ids are marked to be destroy
 * @param {Object} initial
 * @param {Array} initial.featureGroups
 * @param {Object} current
 * @param {Array} current.featureGroups
 */
export const bulkUpdateFeatureGroupsParams = (initial, current) => {
  const result = {};
  const featureGroups = reduceFeatureGroupsDifferences(
    initial.featureGroups,
    current.featureGroups
  );
  if (featureGroups && featureGroups.length) {
    result.featureGroups = featureGroups;
  }

  const features = reduceFeaturesDifferences(
    initial.features,
    current.features
  );

  if (features && features.length) {
    result.features = features;
  }

  const adjustedFeatures = adjustFeaturesForRemovedFeatureGroups({
    features: result.features,
    initialFeatures: initial.features,
    initialFeatureGroups: initial.featureGroups,
    currentFeatureGroups: current.featureGroups
  });

  if (adjustedFeatures && adjustedFeatures.length) {
    result.features = adjustedFeatures;
  }

  return result;
};

const adjustFeaturesForRemovedFeatureGroups = ({
  features,
  initialFeatures,
  initialFeatureGroups,
  currentFeatureGroups
}) => {
  let resultFeatures = features ? [...features] : null;
  const featureGroupsMarkedForRemoval = getMarkedForRemoval(
    initialFeatureGroups,
    currentFeatureGroups
  );

  featureGroupsMarkedForRemoval.forEach((markedFeatureGroup) => {
    const featureGroupMatch = initialFeatureGroups.find(
      ({ uuid }) => uuid === markedFeatureGroup.uuid
    );
    const featureUUIDs = featureGroupMatch && featureGroupMatch.feature_uuids;
    if (featureUUIDs && featureUUIDs.length) {
      featureUUIDs.forEach((uuid) => {
        const featureMatch = initialFeatures.find(
          (feature) => feature.uuid === uuid
        );

        if (featureMatch) {
          if (!resultFeatures) {
            resultFeatures = [];
          }

          const conflictIx = resultFeatures.findIndex(
            (feature) => feature.uuid === featureMatch.uuid
          );
          const markedFeature = markForRemoval(featureMatch);
          if (conflictIx === -1) {
            resultFeatures.push(markedFeature);
          } else {
            /**
             * If an updated or existing marked for deletion model exists
             * replace it with the marked for deletion to ensure its deleted
             */
            resultFeatures.splice(conflictIx, 1, markedFeature);
          }
        }
      });
    }
  });
  return resultFeatures;
};

const CompareTableHeader = ({ dataColumn }) => {
  if (dataColumn.model === FEATURE) {
    return <div className="f3">{dataColumn.header}</div>;
  } else {
    return <div className="f5">{dataColumn.header}</div>;
  }
};

export const extendFeatureComparisonColumnsAndRows = ({ columns, rows }) => {
  return {
    columns: columns.map((column) => {
      if (column.model === FEATURE) {
        return {
          ...column,
          /* eslint-disable-next-line react/display-name */
          Header: (headerProps) => (
            <CompareTableHeader dataColumn={column} {...headerProps} />
          )
        };
      } else {
        return {
          ...column,
          /* eslint-disable-next-line react/display-name */
          Header: (headerProps) => (
            <CompareTableHeader dataColumn={column} {...headerProps} />
          ),
          /* eslint-disable-next-line react/display-name, react/prop-types */
          Cell: ({ row: { index }, column: { id }, value, setFieldValue }) => {
            return value ? (
              <IoIosCheckmark
                size={40}
                // color={active ? COLORS.blue : COLORS.disabled}
                color={COLORS.blue}
              />
            ) : (
              <span></span>
            );
          }
        };
      }
    }),
    data: rows
  };
};

const PROCESS_KEYS_SPLIT_ARITY = ["orderBy"];

export const applyFormSettingsToCollection = ({ collection, form }) => {
  const collectionToProcess = [...collection];
  const PROCESS_FN_MAP = {
    orderBy
  };

  return Object.keys(form).reduce((memo, key) => {
    const processFn = PROCESS_FN_MAP[key];
    if (processFn) {
      const processConfig = form[key];
      if (PROCESS_KEYS_SPLIT_ARITY.indexOf(key) > -1) {
        memo = processFn(memo, processConfig[0], processConfig[1]);
      } else {
        memo = processFn(memo, processConfig);
      }
    }

    return memo;
  }, collectionToProcess);
};

const SCAFFOLD_TITLE_MAP = {
  1: "One",
  2: "Two",
  3: "Three",
  4: "Four"
};

/**
 * Generate matrix of features for products
 * @param {*} products
 * @param {*} entitlements
 * @returns
 */
export const scaffoldFeatureGroupFeatures = (products, entitlements) => {
  const updatedEntitlements = entitlements ? cloneDeep(entitlements) : {};
  const features = products.map((_, productIx) => {
    const title = SCAFFOLD_TITLE_MAP[productIx + 1];
    let uid = title.toLowerCase();
    if (updatedEntitlements[uid]) {
      uid += random(0, 1001);
    }

    products.forEach((prod, subProdIx) => {
      /*
       * Incrementally enable features for products based on index e.g.
       *  - 0 - 1 - 2
       *  0 x - x - x
       *  1 o - x - x
       *  2 o - o - x
       */
      const enabled = subProdIx >= productIx;
      if (!updatedEntitlements[uid]) {
        updatedEntitlements[uid] = {};
      }
      updatedEntitlements[uid][prod.uid] = {
        enabled,
        value: "",
        tooltip: ""
      };
    });

    return {
      title,
      uid,
      description: "",
      tooltip: "",
      uuid: uuidv4(),
      feature_group_uuids: []
    };
  });

  return {
    features,
    featureGroups: [
      {
        title: "Essentials",
        description: "",
        feature_uuids: features.map(({ uuid }) => uuid),
        uuid: uuidv4()
      }
    ],
    entitlements: updatedEntitlements
  };
};

export const getFeaturesFormInitialValues = ({
  features,
  featureGroups,
  form,
  productLayout,
  entitlements,
  ...remainder
}) => {
  const initialForm = {
    ...form,
    ...{
      orderBy: DEFAULT_ORDER_BY
    }
  };
  const initialFeatures = applyFormSettingsToCollection({
    collection: features,
    form: initialForm
  });
  const initialFeatureGroups = applyFormSettingsToCollection({
    collection: featureGroups,
    form: initialForm
  });

  const mergedEntitlements = features.reduce(
    (memo, feature) => {
      if (feature.uuid) {
        if (!memo[feature.uuid]) {
          memo[feature.uuid] = {};
        }
        productLayout.forEach((product) => {
          const prodId = product.uid;
          if (!memo[feature.uuid][prodId]) {
            memo[feature.uuid][prodId] = getEntitlementConfigTemplate();
          }
        });
      }

      return memo;
    },
    entitlements ? cloneDeep(entitlements) : {}
  );

  return {
    form: initialForm,
    features: initialFeatures,
    featureGroups: initialFeatureGroups,
    productLayout,
    entitlements: mergedEntitlements,
    ...remainder
  };
};

const getUUIDFromID = (id) => {
  const parts = id.split(".");
  return parts.length > 1 ? parts[1] : id;
};

const matchesGroupIdType = (id) =>
  Boolean(id.match(FEATURE_GROUP_LIST_DESTINATIONS.FEATURE_GROUP));

const getFeatureGroup = ({ featureGroups, id }) => {
  const featureGroupUUID = getUUIDFromID(id);
  const featureGroupIx = featureGroups.findIndex(
    ({ uuid }) => uuid === featureGroupUUID
  );

  const featureGroup = featureGroups[featureGroupIx];

  return {
    featureGroupUUID,
    featureGroupIx,
    featureGroup,
    featureGroupFeatureUUIDs: featureGroup.feature_uuids
  };
};

// const getFeatureInGroup = ({ featureGroup, features, index }) => {
//   const featureUUID = featureGroup.feature_uuids[index];
//   const featureIx = features.findIndex(({ uuid }) => uuid === featureUUID);

//   return {
//     featureIx,
//     feature: features[featureIx]
//   };
// };

const removeFromTarget = ({ values, collection, uuid, target, touched }) => {
  const index = collection.findIndex((id) => id === uuid);
  if (index > -1) {
    collection.splice(index, 1);
    touched[target] = true;
    set(values, target, collection);
  }
  return { values, touched };
};

// const addToTarget = ({ values, target, uuid, touched }) => {
//   const collection = [...get(values, target, [])];
//   if (collection && collection.indexOf(uuid) === -1) {
//     collection.push(uuid);
//     touched[target] = true;
//     set(values, target, collection);
//   }

//   return {
//     values,
//     touched
//   };
// };

const insertToTargetAtIndex = ({ values, target, uuid, index, touched }) => {
  const collection = [...get(values, target, [])];
  if (collection && collection.indexOf(uuid) === -1) {
    collection.splice(index, 0, uuid);
    touched[target] = true;
    set(values, target, collection);
  }

  return { values, touched };
};

const insertMultipleToTargetAtIndex = ({
  values,
  target,
  selections,
  index,
  touched
}) => {
  let collection = [...get(values, target, [])];
  const additions = selections.filter(
    (uuid) => collection.indexOf(uuid) === -1
  );

  if (additions.length) {
    collection.splice(index, 0, additions);
    collection = flatten(collection);

    touched[target] = true;
    set(values, target, collection);
  }

  return { values, touched };
};

export const removeFeatureFromFeatureGroup = ({
  values,
  setValues,
  setTouched,
  draggableId,
  sourceDropId
}) => {
  let touched = {};
  const featureUUID = getUUIDFromID(draggableId);
  const featureIndex = values.features.findIndex(
    ({ uuid }) => uuid === featureUUID
  );

  const { featureGroupIx, featureGroup } = getFeatureGroup({
    featureGroups: values.featureGroups,
    id: sourceDropId
  });

  if (featureGroup && featureIndex > -1) {
    const feature = values.features[featureIndex];

    /**
     * REMOVE feature from
     * - featureGroup and
     * - entitlements
     */
    values.entitlements && delete values.entitlements[feature.uuid];
    const groupWithoutFeature = removeFromTarget({
      values,
      collection: featureGroup.feature_uuids,
      uuid: feature.uuid,
      target: `featureGroups[${featureGroupIx}].feature_uuids`,
      touched
    });
    values = groupWithoutFeature.values;
    /**
     * REMOVE feature from features
     */
    values.features.splice(featureIndex, 1);
    touched = groupWithoutFeature.touched;

    setValues(values);
    if (!isEmpty(touched)) {
      setTouched(touched);
    }
  }
};

const addFeatureSelectionsToFeatureGroup = ({
  selections,
  values: formValues,
  setValues,
  setTouched,
  destinationDropId,
  destinationIndex
}) => {
  let values = cloneDeep(formValues);
  let touched = {};

  const { featureGroupIx, featureGroup } = getFeatureGroup({
    featureGroups: values.featureGroups,
    id: destinationDropId
  });

  if (selections.length && featureGroup) {
    /**
     * INSERT feature to the group
     */
    const processedInsertions = insertMultipleToTargetAtIndex({
      values,
      target: `featureGroups[${featureGroupIx}].feature_uuids`,
      selections,
      index: destinationIndex,
      touched
    });
    values = processedInsertions.values;
    touched = processedInsertions.touched;

    setValues(values);
    if (!isEmpty(touched)) {
      setTouched(touched);
    }
  }
};

const addFeatureToFeatureGroup = ({
  values: formValues,
  setValues,
  setTouched,
  destinationDropId,
  destinationIndex,
  draggableId
}) => {
  let values = cloneDeep(formValues);
  let touched = {};

  const featureUUID = getUUIDFromID(draggableId);
  const feature = values.features.find(({ uuid }) => uuid === featureUUID);

  const { featureGroupIx, featureGroup } = getFeatureGroup({
    featureGroups: values.featureGroups,
    id: destinationDropId
  });

  if (feature && featureGroup) {
    /**
     * INSERT feature to the group
     */
    const processedInsertion = insertToTargetAtIndex({
      values,
      target: `featureGroups[${featureGroupIx}].feature_uuids`,
      uuid: feature.uuid,
      index: destinationIndex,
      touched
    });
    values = processedInsertion.values;
    touched = processedInsertion.touched;

    setValues(values);
    if (!isEmpty(touched)) {
      setTouched(touched);
    }
  }
};

const reorderWithinGroup = ({
  values,
  setValues,
  setTouched,
  sourceIndex,
  sourceDropId,
  destinationIndex
}) => {
  const touched = {};

  const { featureGroupIx, featureGroupFeatureUUIDs } = getFeatureGroup({
    featureGroups: values.featureGroups,
    id: sourceDropId
  });

  if (featureGroupIx >= 0) {
    const key = `featureGroups[${featureGroupIx}].feature_uuids`;

    const reorderedUUIDs = reorder(
      featureGroupFeatureUUIDs,
      sourceIndex,
      destinationIndex
    );
    touched[key] = true;

    set(values, key, [...reorderedUUIDs]);
    setValues(values);
    setTouched(touched);
  }
};

/**
 */
export const updateMultipleFeatureGroupFeatures = ({
  selections,
  values,
  setValues,
  setTouched,
  tracking,
  callback = () => {}
}) => ({ source, destination, draggableId }) => {
  tracking && Analytics.trackEventWithData(tracking);

  const droppedOffList = !destination;
  if (droppedOffList) {
    return;
  }
  const updatedValues = { ...values };

  const {
    destinationDropId,
    destinationIndex,
    sourceDropId,
    sourceIndex
  } = deconstructSourceAndDestination({ source, destination });

  const changedList = sourceDropId !== destinationDropId;
  const isGroupSource = matchesGroupIdType(sourceDropId);
  const isGroupDestination = matchesGroupIdType(destinationDropId);

  if (changedList) {
    const FROM_FEATURE_GROUP =
      destinationDropId === FEATURE_GROUP_LIST_DESTINATIONS.FEATURE_LIST;

    const BETWEEN_GROUPS = isGroupSource && isGroupDestination;
    if (BETWEEN_GROUPS) {
      console.warn("Attempted to move between feature groups");
    } else if (FROM_FEATURE_GROUP) {
      removeFeatureFromFeatureGroup({
        values: updatedValues,
        setValues,
        setTouched,
        draggableId,
        sourceDropId
      });
    } else {
      addFeatureSelectionsToFeatureGroup({
        selections,
        values: updatedValues,
        setValues,
        setTouched,
        destinationDropId,
        destinationIndex,
        draggableId
      });
    }
  } else {
    if (isGroupSource) {
      reorderWithinGroup({
        values: updatedValues,
        setValues,
        setTouched,
        sourceIndex,
        sourceDropId,
        destinationIndex
      });
    } else {
      console.log("*** Perform no reordering");
    }
  }
  callback && callback();
};

/**
 * Move features between feature list and the available feature groups
 * We reflect feature and feature group ids in their respective other collection for convenience when moving between lists
 * - The source and destination indexes are being used to know WHICH things are being moved so we need to have uuids on both sides to do the identification
 * - a feature will have a feature group uuid added to it
 * - a feature group will have a feature uuid added to it
 * @param {Object} params - formik inputs - 2nd level inputs from DnD
 */
export const updateFeatureGroupFeatures = ({
  values,
  setValues,
  setTouched
}) => ({ source, destination, draggableId }) => {
  const droppedOffList = !destination;
  if (droppedOffList) {
    return;
  }
  /**
   * Clone deep to prevent mutating the form initial values
   * - which breaks the update comparison check logic for form submission
   */
  const updatedValues = cloneDeep(values);

  const {
    destinationDropId,
    destinationIndex,
    sourceDropId,
    sourceIndex
  } = deconstructSourceAndDestination({ source, destination });

  const changedList = sourceDropId !== destinationDropId;

  const isGroupSource = matchesGroupIdType(sourceDropId);
  const isGroupDestination = matchesGroupIdType(destinationDropId);

  if (changedList) {
    const FROM_FEATURE_GROUP =
      destinationDropId === FEATURE_GROUP_LIST_DESTINATIONS.FEATURE_LIST;

    const BETWEEN_GROUPS = isGroupSource && isGroupDestination;
    if (BETWEEN_GROUPS) {
      console.warn("Attempted to move between feature groups");
    } else if (FROM_FEATURE_GROUP) {
      removeFeatureFromFeatureGroup({
        values: updatedValues,
        setValues,
        setTouched,
        draggableId,
        sourceDropId
      });
    } else {
      addFeatureToFeatureGroup({
        values: updatedValues,
        setValues,
        setTouched,
        destinationDropId,
        destinationIndex,
        draggableId
      });
    }
  } else {
    if (isGroupSource) {
      reorderWithinGroup({
        values: updatedValues,
        setValues,
        setTouched,
        sourceIndex,
        sourceDropId,
        destinationIndex
      });
    } else {
      console.log("*** Perform no reordering");
    }
  }
};

/**
 * Update features between feature groups on preview table
 */
export const updateFeatureGroupTableFeatures = ({
  values,
  setValues,
  setTouched,
  tracking
}) => ({ source, destination, draggableId }) => {
  const droppedOffList = !destination;
  if (droppedOffList) {
    return;
  }
  tracking && Analytics.trackEventWithData(tracking);
  /**
   * Clone deep to prevent mutating the form initial values
   * - which breaks the update comparison check logic for form submission
   */
  const updatedValues = cloneDeep(values);

  const {
    sourceDropId,
    sourceIndex,
    destinationDropId,
    destinationIndex
  } = deconstructSourceAndDestination({ source, destination });

  const changedList = sourceDropId !== destinationDropId;

  if (changedList) {
    /**
     * REMOVE feature from the source feature group
     */
    const {
      featureGroupIx: sourceFeatureGroupIx,
      featureGroup: sourceFeatureGroup
    } = getFeatureGroup({
      featureGroups: values.featureGroups,
      id: sourceDropId
    });
    const updatedSourceFeatureUUIDs = [...sourceFeatureGroup.feature_uuids];
    updatedSourceFeatureUUIDs.splice(sourceIndex, 1);
    updatedValues.featureGroups[
      sourceFeatureGroupIx
    ].feature_uuids = updatedSourceFeatureUUIDs;

    /**
     * ADD feature from the destination feature group
     */
    const {
      featureGroupIx: destinationFeatureGroupIx,
      featureGroup: destinationFeatureGroup
    } = getFeatureGroup({
      featureGroups: values.featureGroups,
      id: destinationDropId
    });

    const featureUUID = getUUIDFromID(draggableId);
    const feature = updatedValues.features.find(
      ({ uuid }) => uuid === featureUUID
    );

    const updatedDestinationFeatureUUIDs = [
      ...destinationFeatureGroup.feature_uuids
    ];
    updatedDestinationFeatureUUIDs.splice(destinationIndex, 0, feature.uuid);
    updatedValues.featureGroups[
      destinationFeatureGroupIx
    ].feature_uuids = updatedDestinationFeatureUUIDs;

    setValues(updatedValues);
    setTouched({ featureGroups: true });
  } else {
    reorderWithinGroup({
      values: updatedValues,
      setValues,
      setTouched,
      sourceIndex,
      sourceDropId,
      destinationIndex
    });
  }
};

export const addFeatureAndAddToFeatureGroup = ({
  featureGroup,
  values,
  setValues,
  products,
  setTouched
}) => {
  return (newFeatures) => {
    const updatedValues = cloneDeep(values);
    const updatedFeatures = updatedValues.features;
    const updatedFeatureGroups = updatedValues.featureGroups;
    const updatedEntitlements = updatedValues.entitlements;

    const groupUUIDS = [...featureGroup.feature_uuids];

    const groupIx = updatedFeatureGroups.findIndex(
      ({ uuid }) => uuid === featureGroup.uuid
    );

    newFeatures.forEach((feature) => {
      updatedFeatures.push(feature);
      if (groupIx > -1) {
        groupUUIDS.push(feature.uuid);
      }
      products.forEach((product) => {
        if (feature.uuid) {
          if (!updatedEntitlements[feature.uuid]) {
            updatedEntitlements[feature.uuid] = {};
          }

          updatedEntitlements[feature.uuid][
            product.id
          ] = getEntitlementConfigTemplate();
        }
      });
    });

    const orderedFeatures = applyFormSettingsToCollection({
      collection: updatedFeatures,
      form: {
        orderBy: DEFAULT_ORDER_BY
      }
    });
    if (groupIx > -1) {
      set(updatedFeatureGroups, `[${groupIx}].feature_uuids`, groupUUIDS);
    }

    set(updatedValues, "features", orderedFeatures);
    set(updatedValues, "featureGroups", updatedFeatureGroups);

    setValues(updatedValues);
    setTouched({
      features: true,
      featureGroups: true
    });
  };
};

export const addFeatures = ({ values, setValues, setFieldTouched }) => {
  return (newFeatures) => {
    const { features } = values;
    const updatedFeatures = [...features];

    newFeatures.forEach((feature) => {
      updatedFeatures.push(feature);
    });

    const orderedFeatures = applyFormSettingsToCollection({
      collection: updatedFeatures,
      form: {
        orderBy: DEFAULT_ORDER_BY
      }
    });

    setValues({
      ...values,
      features: orderedFeatures
    });
    setFieldTouched("features", true);
  };
};

// @deprecated - product_config
export const toggleProductConfig = ({
  values,
  feature,
  product,
  setFieldValue,
  setFieldTouched
}) => (evt) => {
  const { value } = evt.currentTarget;
  const toggleOn = value !== TOGGLE_STATE.YES;

  const featureIndex = values.features.findIndex(
    ({ uuid }) => uuid === feature.uuid
  );

  const fieldKey = `features[${featureIndex}].product_config`;
  if (featureIndex > -1) {
    const updatedProductConfig = cloneDeep(get(values, fieldKey));
    const configIx = updatedProductConfig.findIndex(
      ({ uid }) => uid === product.id
    );
    const configPresent = configIx > -1;

    /**
     * If the intent is to turn it on
     * - set it enabled if its present
     * - add it if its not present
     * Else
     * - remove it if its present
     */
    if (toggleOn) {
      if (configPresent) {
        updatedProductConfig[configIx].enabled = true;
      } else {
        updatedProductConfig.push(
          getEntitlementConfigTemplate({ enabled: true })
        );
      }
    } else {
      if (configPresent) {
        updatedProductConfig.splice(configIx, 1);
      }
    }

    setFieldValue(fieldKey, updatedProductConfig);
    setFieldTouched(fieldKey, true);
  }
};

export const toggledFeatureProductEntitlements = ({
  feature,
  products,
  entitlements,
  enabled = false
}) => {
  const result = cloneDeep(entitlements);
  products.forEach(({ id }) => {
    set(result, `${feature.uuid}.${id}.enabled`, enabled);
  });

  return result;
};

export const getAllDisabledProductConfig = (config) =>
  config.map((config) => ({
    ...config,
    enabled: false
  }));

// @deprecated - product_config
export const toggleAllProductConfig = ({
  values,
  feature,
  products,
  setFieldValue,
  setFieldTouched
}) => (evt) => {
  const { value } = evt.currentTarget;
  const enableAll = value !== TOGGLE_STATE.YES;
  const featureIndex = values.features.findIndex(
    ({ uuid }) => uuid === feature.uuid
  );

  const fieldKey = `features[${featureIndex}].product_config`;
  if (featureIndex > -1) {
    const feature = values.features[featureIndex];

    if (enableAll) {
      const updatedProductConfig = toggledFeatureProductEntitlements({
        products,
        config: feature.product_config
      });

      setFieldValue(fieldKey, updatedProductConfig);
      setFieldTouched(fieldKey, true);
    } else if (feature.product_config.length) {
      const updatedProductConfig = getAllDisabledProductConfig(
        feature.product_config
      );

      setFieldValue(fieldKey, updatedProductConfig);
      setFieldTouched(fieldKey, true);
    }
  }
};

export const reorderFeatureProducts = ({
  layout,
  path,
  setFieldValue,
  setFieldTouched
}) => ({ source, destination }) => {
  const { sourceIndex, destinationIndex } = deconstructSourceAndDestination({
    source,
    destination
  });
  const sourceLayout = cloneDeep(layout);

  const reorderedLayout = reorder(sourceLayout, sourceIndex, destinationIndex);

  setFieldValue(path, reorderedLayout);
  setFieldTouched(path, true);
};

export const resetProductFeatureLayouts = (featuresConfig) => {
  return Object.keys(featuresConfig).reduce((memo, key) => {
    const config = featuresConfig[key];
    if (key === COMMON_KEY) {
      memo[key] = config;
    } else {
      memo[key] = {
        ...config,
        layout: []
      };
    }
    return memo;
  }, {});
};

export const anyProductFeatureLayout = (featuresConfig) => {
  let hasLayout = false;
  const keys = Object.keys(featuresConfig);
  for (let keyIx = 0; keyIx < keys.length; keyIx++) {
    const key = keys[keyIx];
    const config = featuresConfig[key];

    if (
      key !== COMMON_KEY &&
      config &&
      config.layout &&
      config.layout.length &&
      !hasLayout
    ) {
      hasLayout = true;
      break;
    }
  }
  return hasLayout;
};

/**
 * User will click on the feature row header to turn on / off all the products in the row
 * @param {Object} feature
 * @param {Array} products
 * @param {Object} entitlements
 * @returns
 */
export const canTurnAllFeatureProductsOffRow = ({
  feature,
  products,
  entitlements
} = {}) => {
  let allEnabled = Boolean(entitlements && entitlements[feature.uuid]);
  if (entitlements) {
    for (let prodIx = 0; prodIx < products.length; prodIx++) {
      const productId = products[prodIx].id;
      const prodEntitlements =
        entitlements[feature.uuid] && entitlements[feature.uuid][productId];
      if (!prodEntitlements || !prodEntitlements.enabled) {
        allEnabled = false;
        break;
      }
    }
  }

  return allEnabled;
};

/**
 * User will click on the product column header to turn on / off all the features in the column
 * @param {Array} features
 * @param {String} productId
 * @param {Object} entitlements
 * @returns
 */
export const canTurnAllProductFeaturesOffCol = ({
  features,
  productId,
  entitlements
}) => {
  let anyDisabled = false;
  const prodEntitlements = features.reduce((memo, feature) => {
    const prodEntitlement =
      entitlements[feature.uuid] && entitlements[feature.uuid][productId];

    if (prodEntitlement) {
      if (!prodEntitlement.enabled && !anyDisabled) {
        anyDisabled = true;
      }
      memo.push(prodEntitlement);
    }

    return memo;
  }, []);
  const allEnabled = !anyDisabled;
  const allAllHaveConfig = prodEntitlements.length === features.length;

  return allAllHaveConfig && allEnabled;
};

export const toggleAllGroupFeaturesForProduct = ({
  entitlements,
  productId,
  features,
  toggleAllOff
}) => {
  const result = { ...entitlements };
  features.forEach(({ uuid }) => {
    result[uuid][productId] = {
      ...result[uuid][productId],
      enabled: !toggleAllOff
    };
  });
  return result;
};

export const prepareNewFeature = (values) => ({
  ...values,
  uid: snakeCase(values.title),
  uuid: getUUID()
});
